"use client";

import { Suspense } from "react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { t } from "@/content/i18n/messages";
import {
  getLocaleFromPathname,
  getLocalizedPathname,
  type Locale,
} from "@/lib/i18n";
import type { BlogPostListEntry } from "@/content/blog/types";

interface BlogProps {
  readonly posts: readonly BlogPostListEntry[];
}

const POSTS_PER_PAGE = 9;

// Splits a translated headline on the [[...]] accent marker so translators can
// reorder words freely; the marked phrase gets the emphasis underline.
function renderAccent(text: string) {
  return text.split(/\[\[(.+?)\]\]/g).map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className="emphasis-underline">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

export function Blog({ posts }: BlogProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname ?? "/");

  const sortedPosts = [...posts].sort(
    (firstPost, secondPost) =>
      new Date(secondPost.date).getTime() - new Date(firstPost.date).getTime(),
  );

  return (
    <div data-i18n-skip className="bg-background">
      <BlogIndexHero locale={locale} />
      <section className="bg-background pb-24">
        <Suspense
          fallback={
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <BlogListSkeleton />
            </div>
          }
        >
          <BlogIndexList posts={sortedPosts} locale={locale} />
        </Suspense>
      </section>
    </div>
  );
}

function BlogIndexHero({ locale }: { readonly locale: Locale }) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section className="bg-background pt-24 pb-16 sm:pt-28 lg:pt-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Hero entrance moves only: opacity stays 1 so the hero paints
            before hydration (LCP). */}
        <motion.div
          initial={prefersReducedMotion ? false : { y: 12 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, ease: [0.21, 0.6, 0.35, 1] }}
        >
          <nav
            aria-label={t(locale, "Breadcrumb")}
            className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-neutral-muted"
          >
            <Link
              href={getLocalizedPathname("/", locale)}
              className="transition-colors duration-200 hover:text-primary"
            >
              {t(locale, "Home")}
            </Link>
            <span className="mx-2" aria-hidden>
              /
            </span>
            <span className="text-neutral-emphasis">{t(locale, "Blog")}</span>
          </nav>

          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "Insights")}
          </p>
          <h1 className="font-display text-5xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-6xl lg:text-7xl">
            {renderAccent(t(locale, "[[Blog]]."))}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
            {t(
              locale,
              "Practical guides on AI agents, software delivery, and digital growth for Malaysian businesses.",
            )}
          </p>
        </motion.div>
      </div>
    </section>
  );
}

interface BlogIndexListProps {
  readonly posts: readonly BlogPostListEntry[];
  readonly locale: Locale;
}

function BlogIndexList({ posts, locale }: BlogIndexListProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // Read current page from URL, default to 1
  const pageParam = searchParams?.get("page");
  const parsedPage = pageParam ? parseInt(pageParam, 10) : 1;
  // Validate parsed value (handles NaN, negative numbers)
  const validPage =
    Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;

  // Empty state
  if (posts.length === 0) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-12 text-center sm:px-6 lg:px-8">
        <p className="text-lg text-neutral-muted">
          {t(locale, "No blog posts found.")}
        </p>
      </div>
    );
  }

  const [featuredPost, ...remainingPosts] = posts;

  // Calculate pagination over the non-featured posts
  const totalPages =
    remainingPosts.length === 0
      ? 1
      : Math.ceil(remainingPosts.length / POSTS_PER_PAGE);

  // Clamp current page to valid range
  const currentPage = Math.max(1, Math.min(totalPages, validPage));

  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const endIndex = startIndex + POSTS_PER_PAGE;
  const currentPosts = remainingPosts.slice(startIndex, endIndex);

  // Handle page change - update URL and scroll to top
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) {
      return;
    }

    const params = new URLSearchParams(searchParams?.toString() ?? "");

    if (page === 1) {
      params.delete("page"); // Clean URL for page 1
    } else {
      params.set("page", String(page));
    }

    const queryString = params.toString();
    router.push(`${pathname}${queryString ? `?${queryString}` : ""}`, {
      scroll: false,
    });

    // Scroll to top after navigation
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      {currentPage === 1 && (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FeaturedPostCard post={featuredPost} locale={locale} />
        </div>
      )}

      {currentPosts.length > 0 && (
        <div
          className={`bg-neutral-surface py-16 lg:py-20 ${
            currentPage === 1 ? "mt-16" : ""
          }`}
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <p className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "All posts")}
            </p>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {currentPosts.map((post, index) => (
                <PostCard
                  key={post.slug}
                  post={post}
                  locale={locale}
                  index={index}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <BlogIndexPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                locale={locale}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

interface FeaturedPostCardProps {
  readonly post: BlogPostListEntry;
  readonly locale: Locale;
}

function FeaturedPostCard({ post, locale }: FeaturedPostCardProps) {
  const prefersReducedMotion = useReducedMotion();
  const featuredImage = post.heroImage || post.thumbnailImage;
  const postHref = getLocalizedPathname(`/blog/${post.slug}/`, locale);

  return (
    <motion.article
      initial={prefersReducedMotion ? false : { opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: [0.21, 0.6, 0.35, 1] }}
      className="group"
    >
      <div className="grid overflow-hidden rounded-2xl border border-border bg-card transition-colors duration-200 hover:border-neutral-border lg:grid-cols-2">
        {featuredImage && (
          <div className="relative h-64 overflow-hidden bg-neutral-surface lg:h-full lg:min-h-[360px]">
            <img
              src={featuredImage}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              width={800}
              height={448}
              decoding="async"
            />
          </div>
        )}
        <div className="flex flex-col p-8 lg:p-10">
          <p className="mb-5 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "Featured")}
          </p>

          <h2 className="mb-4 font-display text-2xl font-bold leading-tight tracking-tight text-neutral-emphasis md:text-3xl">
            <Link
              href={postHref}
              prefetch={false}
              className="transition-colors duration-200 hover:text-primary"
            >
              {post.title}
            </Link>
          </h2>

          <div className="mb-4 flex items-center gap-2 font-mono text-xs text-neutral-muted">
            <time dateTime={post.date}>{post.date}</time>
            <span aria-hidden>•</span>
            <span>{post.readingTime}</span>
          </div>

          <div className="mb-5 flex flex-wrap gap-2">
            {post.labels.map((label) => (
              <span
                key={label}
                className="rounded-full border border-border px-2.5 py-0.5 font-mono text-xs text-neutral-muted"
              >
                {label}
              </span>
            ))}
          </div>

          <p className="mb-8 leading-relaxed text-neutral-muted">
            {post.description}
          </p>

          <Link
            href={postHref}
            prefetch={false}
            className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-300 group-hover:gap-2.5"
          >
            {t(locale, "Read article")}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}

interface PostCardProps {
  readonly post: BlogPostListEntry;
  readonly locale: Locale;
  readonly index: number;
}

function PostCard({ post, locale, index }: PostCardProps) {
  const prefersReducedMotion = useReducedMotion();
  const cardImage = post.thumbnailImage || post.heroImage;
  const postHref = getLocalizedPathname(`/blog/${post.slug}/`, locale);

  return (
    <motion.article
      initial={prefersReducedMotion ? false : { opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{
        duration: 0.6,
        delay: (index % 3) * 0.1,
        ease: [0.21, 0.6, 0.35, 1],
      }}
      className="group h-full"
    >
      <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-colors duration-200 hover:border-neutral-border">
        {cardImage && (
          <div className="relative h-48 overflow-hidden bg-neutral-surface">
            <img
              src={cardImage}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              loading="eager"
              width={800}
              height={448}
              decoding="async"
            />
          </div>
        )}
        <div className="flex flex-1 flex-col p-6">
          <h2 className="mb-3 font-display text-lg font-semibold leading-snug text-neutral-emphasis">
            <Link
              href={postHref}
              prefetch={false}
              className="transition-colors duration-200 hover:text-primary"
            >
              {post.title}
            </Link>
          </h2>

          <div className="mb-3 flex items-center gap-2 font-mono text-xs text-neutral-muted">
            <time dateTime={post.date}>{post.date}</time>
            <span aria-hidden>•</span>
            <span>{post.readingTime}</span>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            {post.labels.map((label) => (
              <span
                key={label}
                className="rounded-full border border-border px-2.5 py-0.5 font-mono text-xs text-neutral-muted"
              >
                {label}
              </span>
            ))}
          </div>

          <p className="mb-5 text-sm leading-relaxed text-neutral-muted line-clamp-2">
            {post.description}
          </p>

          <Link
            href={postHref}
            prefetch={false}
            className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all duration-300 group-hover:gap-2.5"
          >
            {t(locale, "Read article")}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}

interface BlogIndexPaginationProps {
  readonly currentPage: number;
  readonly totalPages: number;
  readonly onPageChange: (page: number) => void;
  readonly locale: Locale;
}

function BlogIndexPagination({
  currentPage,
  totalPages,
  onPageChange,
  locale,
}: BlogIndexPaginationProps) {
  // Generate page numbers with ellipsis for large page counts
  const getPageNumbers = (): (number | "ellipsis")[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | "ellipsis")[] = [1];

    if (currentPage > 3) {
      pages.push("ellipsis");
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) {
      pages.push("ellipsis");
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  // Compact list for mobile: first, current, and last page only, with
  // ellipsis (or the single in-between page) bridging any gaps.
  const getMobilePageNumbers = (): (number | "ellipsis")[] => {
    const anchorPages = Array.from(
      new Set([1, currentPage, totalPages]),
    ).sort((a, b) => a - b);

    const pages: (number | "ellipsis")[] = [];

    anchorPages.forEach((page, index) => {
      if (index > 0) {
        const previousPage = anchorPages[index - 1];
        if (page - previousPage === 2) {
          pages.push(previousPage + 1);
        } else if (page - previousPage > 2) {
          pages.push("ellipsis");
        }
      }
      pages.push(page);
    });

    return pages;
  };

  const pageButtonBaseClasses =
    "inline-flex items-center justify-center rounded-full border font-mono text-sm transition-all duration-300";
  const arrowButtonClasses = `${pageButtonBaseClasses} gap-1 border-border bg-card text-neutral-muted hover:border-neutral-border hover:text-neutral-emphasis disabled:pointer-events-none disabled:opacity-40`;
  const numberButtonClasses = (page: number) =>
    `${pageButtonBaseClasses} ${
      currentPage === page
        ? "border-primary/40 bg-primary/10 text-primary"
        : "border-border bg-card text-neutral-muted hover:border-neutral-border hover:text-neutral-emphasis"
    }`;

  return (
    <nav aria-label={t(locale, "Blog pagination")} className="mt-16">
      {/* Mobile: single compact row — icon arrows + first/current/last pages */}
      <div className="flex items-center justify-between gap-2 sm:hidden">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label={t(locale, "Previous page")}
          className={`${arrowButtonClasses} h-9 min-w-9 px-2`}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>

        <div className="flex items-center gap-1.5">
          {getMobilePageNumbers().map((page, index) =>
            page === "ellipsis" ? (
              <span
                key={`m-ellipsis-${index}`}
                className="px-1 font-mono text-sm text-neutral-muted"
                aria-hidden
              >
                …
              </span>
            ) : (
              <button
                key={`m-page-${page}`}
                type="button"
                onClick={() => onPageChange(page)}
                aria-label={`${t(locale, "Page")} ${page}`}
                aria-current={currentPage === page ? "page" : undefined}
                className={`${numberButtonClasses(page)} h-9 min-w-9 px-2`}
              >
                {page}
              </button>
            ),
          )}
        </div>

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label={t(locale, "Next page")}
          className={`${arrowButtonClasses} h-9 min-w-9 px-2`}
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {/* Desktop: labelled arrows + full page list */}
      <div className="hidden flex-wrap items-center justify-center gap-2 sm:flex">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label={t(locale, "Previous page")}
          className={`${arrowButtonClasses} h-10 min-w-10 px-3`}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          {t(locale, "Previous")}
        </button>

        {getPageNumbers().map((page, index) =>
          page === "ellipsis" ? (
            <span
              key={`ellipsis-${index}`}
              className="px-2 font-mono text-sm text-neutral-muted"
              aria-hidden
            >
              …
            </span>
          ) : (
            <button
              key={`page-${page}`}
              type="button"
              onClick={() => onPageChange(page)}
              aria-label={`${t(locale, "Page")} ${page}`}
              aria-current={currentPage === page ? "page" : undefined}
              className={`${numberButtonClasses(page)} h-10 min-w-10 px-3`}
            >
              {page}
            </button>
          ),
        )}

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label={t(locale, "Next page")}
          className={`${arrowButtonClasses} h-10 min-w-10 px-3`}
        >
          {t(locale, "Next")}
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </nav>
  );
}

function BlogListSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 9 }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse overflow-hidden rounded-2xl border border-border bg-card"
        >
          <div className="h-48 bg-neutral-surface" />
          <div className="space-y-3 p-6">
            <div className="h-6 w-3/4 rounded bg-neutral-surface" />
            <div className="h-4 w-1/2 rounded bg-neutral-surface" />
            <div className="h-4 w-full rounded bg-neutral-surface" />
            <div className="h-4 w-2/3 rounded bg-neutral-surface" />
          </div>
        </div>
      ))}
    </div>
  );
}
